import { useState, useCallback, useEffect } from 'react';
import { Form, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

/**
 * Custom hook for managing shop data and operations
 * @param {Object} options - Hook options
 * @param {string} options.filter - Filter type (approved, pending, suspended)
 * @param {string} options.app_hostname - API hostname
 * @param {Object} options.permitdata - Authentication data containing token
 * @returns {Object} Shop management state and functions
 */
const DEFAULT_FULFILLMENT_CONFIG = {
  provider_type: '',
  is_active: false,
  config_json: {
    api_token: '',
    warehouse_id: 0,
    express_id: 0,
    sync_interval: '1h',
  },
};

export const useShopManagement = ({ filter, app_hostname, permitdata }) => {
  const [form] = Form.useForm();

  const [filtertext, setFiltertext] = useState('');
  const [userFiltered, setUserFiltered] = useState([]);
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [useritem, setUseritem] = useState({});
  const [fileselected, setFileselected] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalUserOpen, setIsModalUserOpen] = useState(false);
  const [isModalFileOpen, setIsModalFileOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [hasCategoryChanges, setHasCategoryChanges] = useState(false);
  const [fulfillmentConfig, setFulfillmentConfig] = useState({ ...DEFAULT_FULFILLMENT_CONFIG });
  const [fulfillmentLoading, setFulfillmentLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    const checkCategoryChanges = (updatedCategories) => {
      if (!updatedCategories || !originalCategories) return false;
      if (updatedCategories.length !== originalCategories.length) return true;

      for (let i = 0; i < updatedCategories.length; i++) {
        const updated = updatedCategories[i];
        const original = originalCategories.find((cat) => cat.category_id === updated.category_id);

        if (!original) return true;
        if (updated.is_active_as_buyer !== original.is_active_as_buyer) return true;
        if (updated.is_active_as_seller !== original.is_active_as_seller) return true;
      }

      return false;
    };

    if (originalCategories.length > 0 && useritem.categories && useritem.categories.length > 0) {
      const hasChanges = checkCategoryChanges(useritem.categories);
      setHasCategoryChanges(hasChanges);
    }
  }, [useritem.categories, originalCategories]);

  const getAllCategories = useCallback(async () => {
    try {
      const response = await axios.post(app_hostname + '/api/product/category/getlist', null, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + permitdata.token,
        },
      });

      if (response.data.status === 'OK') {
        setAllCategories(() => response.data.products);
      } else {
        message.error(response.data.message || 'เกิดข้อผิดพลาด');
      }
    } catch (err) {
      message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
    }
  }, [app_hostname, permitdata.token]);

  const fetchFulfillmentConfig = useCallback(
    async (shopId) => {
      setFulfillmentConfig({ ...DEFAULT_FULFILLMENT_CONFIG });
      setFulfillmentLoading(true);
      try {
        const response = await axios.get(`${app_hostname}/api/order/fulfillment/config/${shopId}`, {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + permitdata.token,
          },
        });
        if (response.data.status === 'OK' && response.data.config) {
          setFulfillmentConfig(response.data.config);
        }
      } catch (err) {
        console.warn('[Fulfillment] Failed to fetch config:', err.message);
      } finally {
        setFulfillmentLoading(false);
      }
    },
    [app_hostname, permitdata.token],
  );

  const getUserList = useCallback(async () => {
    setUsers([]);
    setUserFiltered([]);
    setLoading(true);

    if (allCategories.length === 0) {
      await getAllCategories();
    }

    const formData = new FormData();
    formData.append('filter', filter);

    axios
      .post(app_hostname + '/api/account/shop/getlist', formData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
          Authorization: 'Bearer ' + permitdata.token,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.status === 'OK') {
          const processedUsers = response.data.users.map((user) => {
            if (!user.categories) {
              user.categories = [];
            }
            const existingCategoryIds = user.categories.map((cat) => cat.category_id);
            const mergedCategories = [...user.categories];
            allCategories.forEach((category) => {
              if (!existingCategoryIds.includes(category.productcategory_id)) {
                mergedCategories.push({
                  category_id: category.productcategory_id,
                  category_name: category.productcategory_name,
                  is_active_as_buyer: false,
                  is_active_as_seller: false,
                  category_sort_no: category.productcategory_sortno,
                });
              }
            });
            const sortedProducts = [...mergedCategories].sort(
              (a, b) => a.category_sort_no - b.category_sort_no,
            );
            return {
              ...user,
              categories: sortedProducts,
              sell_license_ids: Array.isArray(user.sell_license_ids) ? user.sell_license_ids : [],
            };
          });
          setUsers(processedUsers);
        } else {
          message.error(response.data.message || 'เกิดข้อผิดพลาด');
        }
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      });
  }, [app_hostname, filter, permitdata.token, allCategories, getAllCategories]);

  useEffect(() => {
    getUserList();
  }, [filter, getUserList]);

  useEffect(() => {
    if (filtertext !== '') {
      const q = filtertext.toLowerCase();
      setUserFiltered(
        users.filter(
          (item) =>
            (item.shop_id && item.shop_id.toLowerCase().includes(q)) ||
            (item.shop_name && item.shop_name.toLowerCase().includes(q)) ||
            (item.shop_email && item.shop_email.toLowerCase().includes(q)) ||
            (item.shop_province && item.shop_province.toLowerCase().includes(q)) ||
            (item.shop_telno && item.shop_telno.toLowerCase().includes(q)) ||
            (item.shop_firstname && item.shop_firstname.toLowerCase().includes(q)) ||
            (item.shop_lastname && item.shop_lastname.toLowerCase().includes(q)) ||
            (item.shop_username && item.shop_username.toLowerCase().includes(q)),
        ),
      );
    } else {
      setUserFiltered(users);
    }
  }, [users, filtertext]);

  const handleCategoryChange = (categoryId, field, value) => {
    setUseritem((prev) => {
      const updatedCategories = prev.categories.map((cat) => {
        if (cat.category_id === categoryId) {
          return { ...cat, [field]: value };
        }
        return cat;
      });

      return {
        ...prev,
        categories: updatedCategories,
      };
    });
  };

  const manageUsers = (userdata) => {
    setUseritem(userdata);
    if (userdata.categories) {
      setOriginalCategories(JSON.parse(JSON.stringify(userdata.categories)));
    } else {
      setOriginalCategories([]);
    }
    setHasCategoryChanges(false);
    const cleanVal = (val) => (val === '-' || val === null || val === undefined ? '' : val);

    form.setFieldsValue({
      shop_firstname: cleanVal(userdata.shop_firstname),
      shop_lastname: cleanVal(userdata.shop_lastname),
      shop_email: cleanVal(userdata.shop_email),
      shop_username: cleanVal(userdata.shop_username),
      shop_name: cleanVal(userdata.shop_name),
      shop_address: cleanVal(userdata.shop_address),
      shop_subdistrict: cleanVal(userdata.shop_subdistrict),
      shop_district: cleanVal(userdata.shop_district),
      shop_province: cleanVal(userdata.shop_province),
      shop_postcode: cleanVal(userdata.shop_postcode),
      shop_telno: cleanVal(userdata.shop_telno),
      bank_name: userdata.bank_name && userdata.bank_name !== '-' ? userdata.bank_name : undefined,
      bank_acc_id: cleanVal(userdata.bank_acc_id),
      bank_acc_name: cleanVal(userdata.bank_acc_name),
      status: userdata.status,
      shopdocument: userdata.shopdocument,
    });
    setIsModalUserOpen(true);
    fetchFulfillmentConfig(userdata.shop_id);
  };

  const handleSubmit = async () => {
    setModalLoading(true);

    // getFieldsValue(true) reads from Form internal store, including fields
    // in Collapse.Panel that haven't been mounted yet (unlike onFinish values).
    // useritem acts as last-resort fallback in case a field was never set.
    const formValues = form.getFieldsValue(true);

    const formData = new FormData();
    formData.append('shop_id', useritem.shop_id);
    formData.append('status', formValues.status ?? useritem.status ?? '');
    formData.append('shopdocument', formValues.shopdocument ?? useritem.shopdocument ?? '');
    formData.append('bank_name', formValues.bank_name ?? useritem.bank_name ?? '');
    formData.append('bank_acc_name', formValues.bank_acc_name ?? useritem.bank_acc_name ?? '');
    formData.append('bank_acc_id', formValues.bank_acc_id ?? useritem.bank_acc_id ?? '');
    formData.append('filter', filter);
    formData.append('shop_firstname', formValues.shop_firstname ?? useritem.shop_firstname ?? '');
    formData.append('shop_lastname', formValues.shop_lastname ?? useritem.shop_lastname ?? '');
    formData.append('shop_email', formValues.shop_email ?? useritem.shop_email ?? '');
    formData.append('shop_username', formValues.shop_username ?? useritem.shop_username ?? '');
    formData.append('shop_name', formValues.shop_name ?? useritem.shop_name ?? '');
    formData.append('shop_address', formValues.shop_address ?? useritem.shop_address ?? '');
    formData.append('shop_province', formValues.shop_province ?? useritem.shop_province ?? '');
    formData.append('shop_district', formValues.shop_district ?? useritem.shop_district ?? '');
    formData.append(
      'shop_subdistrict',
      formValues.shop_subdistrict ?? useritem.shop_subdistrict ?? '',
    );
    formData.append('shop_postcode', formValues.shop_postcode ?? useritem.shop_postcode ?? '');
    formData.append('shop_telno', formValues.shop_telno ?? useritem.shop_telno ?? '');
    formData.append('user_type', useritem.user_type);
    formData.append('sell_license_ids', JSON.stringify(useritem.sell_license_ids || []));
    formData.append('update_categories', hasCategoryChanges ? 'true' : 'false');

    if (hasCategoryChanges && useritem.categories && Array.isArray(useritem.categories)) {
      const categoriesToSend = useritem.categories.map((items) => ({
        category_id: items.category_id,
        category_name: items.category_name,
        is_active_as_buyer: items.is_active_as_buyer,
        is_active_as_seller: items.is_active_as_seller,
      }));

      formData.append('categories', JSON.stringify(categoriesToSend));
    }

    try {
      const response = await axios.post(app_hostname + '/api/account/shop/edit', formData, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + permitdata.token,
        },
      });

      if (response.data.status === 'OK') {
        // Auto-save fulfillment config if provider is selected
        if (fulfillmentConfig.provider_type) {
          await saveFulfillmentConfig(useritem.shop_id, fulfillmentConfig);
        }
        setModalLoading(false);
        message.success('อัพเดทข้อมูล Shop สำเร็จ');
        await getUserList();
        setIsModalUserOpen(false);
        setUseritem({});
        setFulfillmentConfig({ ...DEFAULT_FULFILLMENT_CONFIG });
        setHasCategoryChanges(false);
      } else {
        setModalLoading(false);
        message.error(response.data.message || 'เกิดข้อผิดพลาด');
      }
    } catch (err) {
      setModalLoading(false);
      message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
    }
  };

  const saveFulfillmentConfig = useCallback(
    async (shopId, config) => {
      try {
        const response = await axios.post(
          `${app_hostname}/api/order/fulfillment/config`,
          { shop_id: shopId, ...config },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + permitdata.token,
            },
          },
        );
        if (response.data.status === 'OK') {
          message.success('บันทึกตั้งค่า Fulfillment สำเร็จ');
        } else {
          message.error(response.data.message || 'บันทึกตั้งค่า Fulfillment ล้มเหลว');
        }
      } catch (err) {
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      }
    },
    [app_hostname, permitdata.token],
  );

  const readfile = async (userdata) => {
    setLoading(true);

    setUseritem(userdata);

    const formData = new FormData();
    formData.append('shop_id', userdata.shop_id);

    axios
      .post(app_hostname + '/api/account/shop/getfiles', formData, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + permitdata.token,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.status === 'OK') {
          setFiles(response.data.files ?? []);
        } else {
          // Handle case where folder doesn't exist (no uploads yet)
          setFiles([]);
        }
        setIsModalFileOpen(true);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      });
  };

  const selectFilename = (filename) => {
    const stamp = dayjs(new Date()).format('DDMMYYYYHHmmss');
    const tmp = stamp + '_' + filename;
    setFileselected(tmp);
  };

  const downloadfile = async (id) => {
    const stamp = id + '_' + dayjs(new Date()).format('DDMMYYYYHHmmss');
    if (files.length > 0) {
      const formData = new FormData();
      formData.append('shop_id', useritem.shop_id);
      if (files.length > 0) {
        formData.append('file1', files[0].filename);
      }
      if (files.length > 1) {
        formData.append('file2', files[1].filename);
      }
      if (files.length > 2) {
        formData.append('file3', files[2].filename);
      }
      if (files.length > 3) {
        formData.append('file4', files[3].filename);
      }

      axios
        .post(app_hostname + '/api/account/shop/downloadzipfile', formData, {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + permitdata.token,
          },
          responseType: 'arraybuffer',
        })
        .then((response) => {
          const url = window.URL.createObjectURL(
            new Blob([response.data], { type: 'application/zip' }),
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', stamp + '.zip');
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        })
        .catch((err) => {
          message.error(err.message || 'เกิดข้อผิดพลาด');
        });
    } else {
      message.error('ไม่พบไฟล์');
    }
  };

  const convertJSONToCSV = (jsonData, columnHeaders) => {
    if (jsonData.length === 0) {
      return '';
    }

    const headers = columnHeaders.join(',') + '\n';
    const rows = jsonData
      .map((row) => {
        return columnHeaders.map((field) => row[field] || '').join(',');
      })
      .join('\n');

    return headers + rows;
  };

  const getCSVFilename = () => {
    switch (filter) {
      case 'approved':
        return 'meya_account_approved.csv';
      case 'pending':
        return 'meya_account_pending.csv';
      case 'suspended':
        return 'meya_account_suspend.csv';
      default:
        return 'meya_account.csv';
    }
  };

  const downloadCSV = () => {
    const headers = [
      'shop_id',
      'shop_email',
      'shop_username',
      'shop_firstname',
      'shop_lastname',
      'shop_name',
      'shop_address',
      'shop_country',
      'shop_province',
      'shop_district',
      'shop_subdistrict',
      'shop_postcode',
      'userrole',
      'status',
      'lastlogin',
      'createdate',
      'approvedate',
      'shopdocument',
      'bank_name',
      'bank_acc_name',
      'bank_acc_id',
    ];
    const csvData = convertJSONToCSV(users, headers);

    if (csvData === '') {
      message.warning('ไม่มีข้อมูลสำหรับส่งออก');
    } else {
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', getCSVFilename());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleUploadDoc = async (uploadedFile, fileKey) => {
    const url = app_hostname + `/api/account/shop/${useritem.shop_id}/docs/${fileKey}/reupload`;
    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const response = await axios.post(url, formData, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + permitdata.token,
        },
      });

      if (response.data.status === 'OK') {
        message.success(`อัพโหลด ${fileKey} สำเร็จ`);
        setFileselected('');

        const formData2 = new FormData();
        formData2.append('shop_id', useritem.shop_id);
        const res = await axios.post(app_hostname + '/api/account/shop/getfiles', formData2, {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + permitdata.token,
          },
        });
        if (res.data.status === 'OK') {
          setFiles(res.data.files ?? []);
        }
      } else {
        message.error(response.data.message || 'อัพโหลดล้มเหลว');
      }
    } catch (error) {
      message.error('อัพโหลดไฟล์ล้มเหลว');
    }
  };

  const closeUserModal = () => {
    setIsModalUserOpen(false);
    setUseritem({});
    setFulfillmentConfig({ ...DEFAULT_FULFILLMENT_CONFIG });
    setHasCategoryChanges(false);
  };

  const closeFileModal = () => {
    setIsModalFileOpen(false);
    setFileselected('');
    setFiles([]);
  };

  return {
    form,
    loading,
    modalLoading,
    users,
    userFiltered,
    useritem,
    files,
    fileselected,
    filtertext,
    isModalUserOpen,
    isModalFileOpen,
    hasCategoryChanges,
    currentPage,
    pageSize,
    setFiltertext,
    setUseritem,
    setCurrentPage,
    setPageSize,
    getUserList,
    manageUsers,
    handleSubmit,
    handleCategoryChange,
    readfile,
    selectFilename,
    downloadfile,
    downloadCSV,
    handleUploadDoc,
    closeUserModal,
    closeFileModal,
    app_hostname,
    fulfillmentConfig,
    setFulfillmentConfig,
    fetchFulfillmentConfig,
    saveFulfillmentConfig,
    fulfillmentLoading,
  };
};
